import { focusManager, MutationCache, QueryClient, QueryKey } from '@tanstack/react-query';

import toast from 'react-hot-toast';

import { FetchResponse, getErrorMessage } from 'shared/base';

import { mutationOperationConfig } from './mutationOperationConfig';

const retryCodes = new Set([408, 413, 429, 500, 502, 503, 504, 521, 522, 524]);

const mutationCache = new MutationCache({
  onError: (response, payload, ___, mutation) => {
    const mutationObservers = (mutation as any).observers as MutationObserver[];
    const hasOnError =
      !!mutation.options?.onError ||
      mutationObservers.some(
        (observer: any) => observer?.options?.onError || observer?.mutateOptions?.onError,
      );

    if (hasOnError) {
      return;
    }

    const mutationKey = mutation.options.mutationKey?.[0] as string;
    const mutationConfig = mutationKey
      ? mutationOperationConfig[mutationKey as keyof typeof mutationOperationConfig]
      : undefined;

    if (mutationConfig) {
      const toastText =
        typeof mutationConfig.errorMessage === 'function'
          ? mutationConfig.errorMessage({ payload })
          : mutationConfig.errorMessage;

      if (!toastText) {
        return;
      }

      const errorMessage = getErrorMessage(response as FetchResponse<any>) || 'Unknown error';

      toast.remove(mutationKey);
      setTimeout(() => {
        toast.error(`${toastText}:\n${errorMessage}`, {
          id: mutationKey,
        });
      }, 0);
    }
  },
  onSuccess(response, payload, ___, mutation) {
    const mutationObservers = (mutation as any).observers as MutationObserver[];
    const hasOnSuccess =
      !!mutation.options?.onSuccess ||
      mutationObservers.some(
        (observer: any) => observer?.options?.onSuccess || observer?.mutateOptions?.onSuccess,
      );

    const mutationKey = mutation.options.mutationKey?.[0] as string;
    const mutationConfig = mutationKey
      ? mutationOperationConfig[mutationKey as keyof typeof mutationOperationConfig]
      : undefined;

    if (mutationConfig) {
      if (!hasOnSuccess) {
        const toastText =
          typeof mutationConfig.successMessage === 'function'
            ? mutationConfig.successMessage({ payload })
            : mutationConfig.successMessage;

        if (!toastText) {
          return;
        }

        toast.remove(mutationKey);
        setTimeout(() => {
          toast.success(toastText, { id: mutationKey });
        }, 100);
      }

      for (const query of mutationConfig.invalidateQueries) {
        let queryKeys: QueryKey[] = [];
        if (typeof query === 'function') {
          const result = query({
            cachedQueries: queryClient.getQueryCache().findAll(),
            payload,
            response,
          });

          if (result) {
            queryKeys =
              typeof result === 'object' && 'queryKeys' in result ? result.queryKeys : [result];
          }
        } else {
          queryKeys = [query];
        }

        for (const queryKey of queryKeys) {
          queryClient.invalidateQueries({ exact: false, queryKey });
        }
      }
    }
  },
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 5 * 60 * 1000, // 5min, which is the react-query default
      refetchOnWindowFocus: () => {
        // Disable refetchOnWindowFocus for reports
        // as per https://cloudqueryio.slack.com/archives/C08AT9D3K8S/p1747128297088309
        if (typeof window !== 'undefined' && window.location.pathname.includes('/reports')) {
          return false;
        }

        return true;
      },
      retry(failureCount, error: any) {
        return retryCodes.has(error?.response?.status) && failureCount < 3;
      },
    },
  },
  mutationCache,
});

// This is a workaround to prevent refetching on window focus
// when window becomes focused by clicking on a button that triggers
// a query itself. For example, clicking a next page button when window
// is not focused will trigger a refetch while it should make the request
// for the next page.
focusManager.setEventListener((handleFocus) => {
  const delayedHandleFocus = () => {
    setTimeout(handleFocus, 100);
  };
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('visibilitychange', delayedHandleFocus);
    window.addEventListener('focus', delayedHandleFocus);
  }

  return () => {
    window.removeEventListener('visibilitychange', delayedHandleFocus);
    window.removeEventListener('focus', delayedHandleFocus);
  };
});
